import { useEffect, useState } from 'react'
import { PreviewConfirmModal } from 'components/dialog/ModalTemplate'
import CurrencyInput from 'components/form/CurrencyInput'
import {
  integerValue,
  invalidNumberInput,
  isEmptyObject,
  isNullOrUndefined,
} from 'utils'
import useValidator from 'utils/hooks/useValidator'
import classNames from 'classnames'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { fail, failWithErrorResponse, success } from 'features/toast/Toast'
import {
  hideAdGroupMaxBiddingAmountEdit,
  hidePreview,
  showPreview,
  updateAdGroupMaxBiddingAmountEdit,
} from 'features/dashboard/operation/adGroupMaxBiddingAmountEdit/adGroupMaxBiddingAmountEditSlice'
import { Modal } from 'components/dialog/Modals'
import Table from 'components/table/Table'
import { refreshDashboard } from 'features/dashboard/dashboardCommonSlice'
import Loading from 'components/loading/Loading'
import FixedTable from 'components/table/FixedTable'

const AdGroupMaxBiddingAmountEditModal = () => {
  const dispatch = useDispatch()
  const {
    visible,
    isSubmitting,
    previewVisible,
    isLoading,
    adGroups,
    maxBiddingAmount,
    previewMaxBiddingAmount,
    loadingError,
  } = useSelector(
    ({ dashboard }) => dashboard.operation.adGroupMaxBiddingAmountEdit,
    shallowEqual
  )
  const [maxBiddingAmountInput, setMaxBiddingAmountInput] =
    useState(maxBiddingAmount)
  useEffect(() => {
    if (visible) {
      setMaxBiddingAmountInput(maxBiddingAmount)
    }
  }, [visible, maxBiddingAmount])
  const { errors, validate } = useValidator(
    () => ({
      maxBiddingAmount:
        (maxBiddingAmountInput === '' ||
          invalidNumberInput(maxBiddingAmountInput, 100000, 70, 10)) &&
        true,
    }),
    [maxBiddingAmountInput]
  )

  const handlePreview = async () => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        dispatch(showPreview(maxBiddingAmountInput))
      }
    } catch (ignore) {}
  }
  const handleConfirm = async ({ preventClose }) => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        const { error } = await dispatch(updateAdGroupMaxBiddingAmountEdit())
        if (error) {
          preventClose()
          fail(
            '광고그룹 기본 입찰가 변경사항을 적용할 수 없습니다.\n다시 시도해 주세요.'
          )
        } else {
          dispatch(refreshDashboard())
          success('광고그룹 기본 입찰가 변경사항이 적용되었습니다.')
        }
      }
    } catch (error) {
      preventClose()
    }
  }

  useEffect(
    () => () => {
      dispatch(hideAdGroupMaxBiddingAmountEdit())
    },
    [dispatch]
  )
  useEffect(() => {
    if (!isEmptyObject(errors)) {
      dispatch(hidePreview())
    }
  }, [dispatch, errors])
  useEffect(() => {
    if (loadingError) {
      dispatch(hideAdGroupMaxBiddingAmountEdit())
      failWithErrorResponse(loadingError, {
        messageMap: {
          104007: '삭제된 값이 존재합니다.\n새로고침 후 다시 시도해 주세요.',
        },
      })
    }
  }, [dispatch, loadingError])

  const warningCount = adGroups.filter(
    ({ biddingStrategy: { maxBiddingAmount: before } }) => {
      const after = previewMaxBiddingAmount
      const percentage =
        isNullOrUndefined(before) || isNullOrUndefined(after)
          ? 0
          : (after / before) * 100
      return percentage >= 500
    }
  ).length
  const preview = previewVisible && isEmptyObject(errors)
  const changed = maxBiddingAmountInput !== previewMaxBiddingAmount

  return (
    <Modal on={visible} className="adbudget_layer">
      <PreviewConfirmModal
        title="광고그룹 기본 입찰가 변경"
        isSubmitting={isSubmitting}
        onPreview={handlePreview}
        onConfirm={handleConfirm}
        previewDisabled={!changed && preview}
        confirmDisabled={changed || !preview}
        handleClose={() => dispatch(hideAdGroupMaxBiddingAmountEdit())}>
        <p className="desc_copy">
          선택된 그룹의 기본 입찰가가 설정된 금액으로 일괄 변경됩니다.
        </p>
        <div className="group_budget">
          {isLoading ? (
            <Loading />
          ) : (
            <>
              <strong className="tit_budget">기본 입찰가</strong>
              <div className="group_item">
                <CurrencyInput
                  min={70}
                  max={100000}
                  step={10}
                  maxLength={6}
                  value={maxBiddingAmountInput}
                  error={errors.maxBiddingAmount}
                  onValueChange={value =>
                    setMaxBiddingAmountInput(integerValue(value))
                  }
                />
              </div>
              <p
                className={classNames({
                  desc_item: isNullOrUndefined(errors.maxBiddingAmount),
                  txt_error: errors.maxBiddingAmount,
                })}>
                광고그룹 기본 입찰가는 최소 70원 ~ 최대 100,000원까지 10원
                단위로 설정할 수 있습니다.
              </p>
              {preview && (
                <>
                  <FixedTable caption="기본 입찰가 변경 이력 표">
                    <Table.Head>
                      <Table.Column
                        title="선택 광고그룹"
                        className="inner_cell"
                        width={194}
                      />
                      <Table.Column
                        title="변경 전"
                        className="inner_cell"
                        width={171}
                      />
                      <Table.Column title="변경 후" className="inner_cell" />
                    </Table.Head>
                    <Table.Body>
                      {adGroups.map(
                        ({
                          id,
                          name,
                          biddingStrategy: { maxBiddingAmount: before },
                        }) => {
                          const after = previewMaxBiddingAmount
                          const percentage =
                            isNullOrUndefined(before) ||
                            isNullOrUndefined(after)
                              ? 0
                              : (after / before) * 100
                          const fixed = Math.ceil(percentage.toFixed(1))
                          return (
                            <Table.Row
                              rowId={id}
                              key={id}
                              className={classNames({
                                update: before !== after,
                              })}>
                              <td>
                                <div className="inner_cell">
                                  <span className="txt_limit2">{name}</span>
                                </div>
                              </td>
                              <td>
                                <div className="inner_cell">
                                  {before.toLocaleString()}원
                                </div>
                              </td>
                              <td>
                                <div className="inner_cell">
                                  {after.toLocaleString()}원
                                  {percentage >= 500 && (
                                    <span className="txt_error">
                                      {fixed.toLocaleString()}%로 증가폭이
                                      큽니다.
                                    </span>
                                  )}
                                </div>
                              </td>
                            </Table.Row>
                          )
                        }
                      )}
                    </Table.Body>
                  </FixedTable>
                  {warningCount > 0 && (
                    <p className="txt_error">
                      변경 전 금액 대비 증가폭이 큰 입력값이 {warningCount}개
                      존재합니다. 입력값이 맞는지 확인하세요.
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </PreviewConfirmModal>
    </Modal>
  )
}
export default AdGroupMaxBiddingAmountEditModal
